// src/helpers/timeHelper.js

/**
 * Formats a 24-hour time string, Date object, or ISO date string into a 12-hour AM/PM format.
 * 
 * Examples:
 *   formatTo12Hour("14:30") => "02:30 PM"
 *   formatTo12Hour("09:05:00") => "09:05 AM"
 *   formatTo12Hour(new Date()) => Current time in AM/PM format
 * 
 * @param {string|Date} inputTime - The time input to be formatted
 * @returns {string} Formatted 12-hour time string (hh:mm AM/PM)
 */
export const formatTo12Hour = (inputTime) => {
  if (!inputTime) return "";

  try {
    let hours = 0;
    let minutes = 0;

    // 1. If input is a Date object
    if (inputTime instanceof Date) {
      hours = inputTime.getHours();
      minutes = inputTime.getMinutes();
    } 
    // 2. If input is an ISO or Full Date String (e.g., "2026-08-07T14:30:00Z")
    else if (typeof inputTime === 'string' && inputTime.includes('T')) {
      const dateObj = new Date(inputTime);
      if (isNaN(dateObj.getTime())) return inputTime; // Return original string if date is invalid
      hours = dateObj.getHours();
      minutes = dateObj.getMinutes();
    } 
    // 3. If input is a standard time string (e.g., "14:30" or "14:30:00")
    else if (typeof inputTime === 'string') {
      const timeParts = inputTime.split(':');
      if (timeParts.length < 2) return inputTime;
      
      hours = parseInt(timeParts[0], 10);
      minutes = parseInt(timeParts[1], 10);
    } else {
      return String(inputTime);
    }

    // Validate bounds for hours and minutes
    if (isNaN(hours) || hours < 0 || hours > 23 || isNaN(minutes) || minutes < 0 || minutes > 59) {
      return String(inputTime);
    }

    // Determine AM/PM marker
    const ampm = hours >= 12 ? "PM" : "AM";

    // Convert to 12-hour standard
    let formattedHours = hours % 12;
    formattedHours = formattedHours ? formattedHours : 12; // '0' hours becomes '12'

    // Add leading zeroes for single-digit numbers (e.g., 9 -> "09")
    const padHours = String(formattedHours).padStart(2, '0');
    const padMinutes = String(minutes).padStart(2, '0');

    return `${padHours}:${padMinutes} ${ampm}`;
  } catch (error) {
    console.error("Time formatting error:", error);
    return String(inputTime);
  }
};